/**
 * 공급사 정보 — 어디로 넣고, 계산서는 누구 앞으로 끊는가
 * ---------------------------------------------------------------------------
 * ## 왜 고객 화면에 있나
 * 결제 수단이 **계좌이체**다. 그런데 어느 계좌로 넣는지가 화면 어디에도
 * 없으면 고객은 청구서를 받고 전화를 걸어 물어야 하고, 불러 주는 번호를
 * 받아 적는 동안 한 자리가 틀리면 **돈이 엉뚱한 곳으로 간다.**
 *
 * 세금계산서를 끊으려면 공급자 사업자등록증도 필요하다. 그것까지 메일로
 * 주고받을 이유가 없다 — 여기서 바로 받는다.
 *
 * ## 계좌번호는 눌러서 복사한다
 * 눈으로 읽어 옮겨 적는 것이 **틀리는 지점**이다. 복사 단추를 둔다.
 */

import { useQuery } from '@tanstack/react-query';
import { Alert, App as AntdApp, Button, Card, Descriptions, Space, Tag, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

export interface SupplierInfo {
  companyName: string;
  bizRegNo: string;
  ceoName: string;
  address: string;
  bizType: string;
  bizItem: string;
  email: string;
  bank: {
    bankName: string;
    accountNo: string;
    accountHolder: string;
    memo: string;
    missing: string[];
  };
  documents: Array<{
    documentId: string;
    docType: string;
    docTypeName: string | null;
    fileName: string;
    byteSize: number;
    downloadable: boolean;
  }>;
}

export function useSupplierInfo() {
  return useQuery<SupplierInfo, NormalizedError>({
    queryKey: ['billing', 'supplier-info'],
    queryFn: async () => (await apiClient.get<SupplierInfo>('/billing/supplier-info')).data,
    // 거의 바뀌지 않는다. 다만 계좌가 바뀌면 빨리 따라와야 한다.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** 사업자등록번호를 읽기 좋게. 10자리가 아니면 손대지 않는다. */
function bizNo(v: string): string {
  return v && v.length === 10 ? `${v.slice(0, 3)}-${v.slice(3, 5)}-${v.slice(5)}` : v || '-';
}

export function SupplierInfoCard() {
  const { message } = AntdApp.useApp();
  const info = useSupplierInfo();
  const s = info.data;

  const copy = async (label: string, value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label}를 복사했습니다.`);
    } catch {
      // 클립보드는 보안 컨텍스트에서만 열린다. 실패해도 값은 화면에 있다.
      message.warning('복사할 수 없습니다. 화면의 값을 직접 선택해 주세요.');
    }
  };

  const download = async (documentId: string, fileName: string): Promise<void> => {
    try {
      const { data } = await apiClient.post<{ url: string }>(
        `/billing/supplier-info/documents/${documentId}/download-url`,
      );
      window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      message.error(`${fileName}을(를) 받지 못했습니다. ${errorText(err as NormalizedError)}`);
    }
  };

  if (info.isError) {
    return (
      <Card size="small" title="입금 계좌와 공급자 정보">
        <Alert type="error" showIcon message={errorText(info.error)} />
      </Card>
    );
  }

  const bankReady = Boolean(s && s.bank.missing.length === 0);

  return (
    <Card size="small" title="입금 계좌와 공급자 정보" loading={info.isLoading}>
      {/* ---- 입금 계좌 ---- */}
      {bankReady ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <Space wrap size={8}>
              <Text strong style={{ fontSize: 15 }}>
                {s!.bank.bankName} {s!.bank.accountNo}
              </Text>
              <Text type="secondary">예금주 {s!.bank.accountHolder}</Text>
              <Button
                size="small"
                onClick={() => void copy('계좌번호', `${s!.bank.bankName} ${s!.bank.accountNo}`)}
              >
                복사
              </Button>
            </Space>
          }
          description={
            s!.bank.memo ? (
              <Text>{s!.bank.memo}</Text>
            ) : (
              <Text type="secondary">청구서에 적힌 금액을 납기까지 위 계좌로 보내 주세요.</Text>
            )
          }
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="입금 계좌가 아직 안내되지 않았습니다"
          description="청구서에 계좌가 함께 적혀 나갑니다. 급하시면 아래 이메일로 문의해 주세요."
        />
      )}

      {/* ---- 계산서 발행에 필요한 공급자 정보 ---- */}
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="상호">{s?.companyName || '-'}</Descriptions.Item>
        <Descriptions.Item label="사업자등록번호">{bizNo(s?.bizRegNo ?? '')}</Descriptions.Item>
        <Descriptions.Item label="대표자">{s?.ceoName || '-'}</Descriptions.Item>
        <Descriptions.Item label="업태 / 종목">
          {[s?.bizType, s?.bizItem].filter(Boolean).join(' / ') || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="사업장 주소" span={2}>
          {s?.address || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="문의" span={2}>
          {s?.email || '-'}
        </Descriptions.Item>
      </Descriptions>

      {/* ---- 증빙 서류 ---- */}
      <Paragraph type="secondary" style={{ fontSize: 12, margin: '12px 0 6px' }}>
        세금계산서는 <b>청구서 발행 뒤</b> 위 공급자 정보로 발행됩니다. 경리 담당자께
        전달하실 서류는 아래에서 받으실 수 있습니다.
      </Paragraph>
      <Space wrap size={8}>
        {(s?.documents ?? []).length === 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            첨부된 서류가 없습니다. 필요하시면 위 이메일로 요청해 주세요.
          </Text>
        )}
        {(s?.documents ?? []).map((d) => (
          <Button
            key={d.documentId}
            icon={<DownloadOutlined />}
            disabled={!d.downloadable}
            onClick={() => void download(d.documentId, d.docTypeName ?? d.fileName)}
          >
            {d.docTypeName ?? d.fileName}
            {!d.downloadable && (
              <Tag style={{ marginLeft: 6 }} color="orange">
                준비 중
              </Tag>
            )}
          </Button>
        ))}
      </Space>
    </Card>
  );
}
